import type { UtmTrackingData } from '@/portals/customer/types/customer-portal';

const UTM_COOKIE_NAME = 'utm_tracking_data';
const UTM_COOKIE_MAX_AGE_SECONDS = 60 * 60; // 1 hour

const setCookie = (name: string, value: string, maxAgeSeconds: number): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax${secure}`;
};

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

type CompleteUtmTrackingData = Required<UtmTrackingData>;

const hasAllUtmValues = (utm?: UtmTrackingData | null): utm is CompleteUtmTrackingData =>
  Boolean(utm) &&
  isNonEmptyString(utm?.utm_source) &&
  isNonEmptyString(utm?.utm_medium) &&
  isNonEmptyString(utm?.utm_campaign);

/**
 * Builds a UTM query-string slug (e.g. "?utm_source=x&utm_medium=y&utm_campaign=z")
 * from already-extracted UTM values. Returns an empty string unless utm_source,
 * utm_medium, and utm_campaign are all present.
 */
export const buildUtmQsParams = (utm?: UtmTrackingData | null): string => {
  if (!utm || !hasAllUtmValues(utm)) return '';
  const { utm_source, utm_medium, utm_campaign } = utm;
  return `?${new URLSearchParams({ utm_source, utm_medium, utm_campaign })}`;
};

/**
 * Extracts UTM tracking values from the given search params (or
 * `window.location.search` if none is provided) into a UtmTrackingData object.
 */
export const getUtmTrackingData = (searchParams?: URLSearchParams): UtmTrackingData => {
  const params = searchParams ?? new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') ?? '',
    utm_medium: params.get('utm_medium') ?? '',
    utm_campaign: params.get('utm_campaign') ?? '',
  };
};

/**
 * Persists the given UTM values in a cookie that expires in 1 hour.
 */
export const setUtmCookie = (utm: UtmTrackingData): void => {
  setCookie(UTM_COOKIE_NAME, JSON.stringify(utm), UTM_COOKIE_MAX_AGE_SECONDS);
};

/**
 * Reads the UTM cookie. Returns null if it's missing, malformed, expired
 * (the browser drops it automatically), or incomplete.
 */
export const getUtmCookie = (): UtmTrackingData | null => {
  try {
    const raw = getCookie(UTM_COOKIE_NAME);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as UtmTrackingData;
    return hasAllUtmValues(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Reads UTM params from the current URL (or the given search params) and,
 * if all three are present, (re)writes the UTM cookie with a fresh 1-hour
 * expiry. Call this on every app load so the cookie always reflects the
 * most recent UTM-tagged hit.
 *
 * Intentional: a partial set (e.g. only utm_source + utm_medium) never
 * touches the cookie, so an incomplete hit can't clobber the last complete
 * attribution. Confirmed as the desired attribution rule.
 */
export const syncUtmCookieFromUrl = (searchParams?: URLSearchParams): void => {
  const utm = getUtmTrackingData(searchParams);
  if (hasAllUtmValues(utm)) {
    setUtmCookie(utm);
  }
};

/**
 * Resolves the UTM values to use, prioritizing a complete set from the
 * given search params (or `window.location.search`); if the URL doesn't
 * have a complete set, falls back to the UTM cookie.
 *
 * Intentional: a partial URL set (missing one of the three params) is
 * treated the same as no URL params and falls back to the cookie, i.e. the
 * last *complete* UTM hit within the 1-hour window wins over a same-session
 * partial hit. Confirmed as the desired attribution rule.
 *
 * This is the single source of truth for URL-then-cookie resolution; use it
 * (directly or via `getUtmQsParams`) anywhere UTM data is needed so the two
 * paths can't drift apart.
 */
export const resolveUtmTrackingData = (searchParams?: URLSearchParams): UtmTrackingData => {
  const fromUrl = getUtmTrackingData(searchParams);
  return hasAllUtmValues(fromUrl) ? fromUrl : (getUtmCookie() ?? fromUrl);
};

/**
 * Builds a UTM query-string slug from `resolveUtmTrackingData`. Returns an
 * empty string if neither the URL nor the cookie has a complete set of UTM
 * values.
 */
export const getUtmQsParams = (searchParams?: URLSearchParams): string =>
  buildUtmQsParams(resolveUtmTrackingData(searchParams));
